import { useModal } from '@ebay/nice-modal-react';
import { addModal } from 'Redux/modules/modal/actions';
import {
  getPendingPrescription,
  voidPrescription,
  contextMenuHide,
} from 'Redux/modules/webChat/actions';
import { switchConsultToOtherClinic } from 'Redux/modules/webChat/consult/actions';
import { useDispatch } from 'react-redux';
import SwitchClinicModal from 'Modules/Prescription/SwitchClinicModal';
import NoPharmacyModal from 'Modules/Prescription/NoPharmacyModal';
import { findClinicIdFromClinicsMapping } from 'Helpers/consult-utils';
import useProfile from 'Hooks/doctor/useDoctorProfile';
import useClinicDetail from 'Containers/Home/hooks/useClinicDetail';
import { getPharmacies } from 'Redux/modules/webChat/pharmacy/actions';
import { WEBCHAT } from 'Constants/normalize-module';
import { updateEntities } from 'Redux/modules/entities/actions';
import { INDONESIA_ISO_CODE } from 'Constants/commons';
import { getCountries } from 'Redux/modules/config/actions';

const removeDuplicateClinics = clinics => {
  return Array.from(new Map(clinics.map(clinic => [clinic.id, clinic])).values());
};

const useIssuePrescription = conversation => {
  const dispatch = useDispatch();
  const { profile } = useProfile();

  const switchClinicModal = useModal(SwitchClinicModal);
  const noPharmacyModal = useModal(NoPharmacyModal);

  const consultId = conversation?.latestConsult?.id;
  const profileId = profile?.id;

  const clinicId = findClinicIdFromClinicsMapping(
    conversation?.latestConsult?.clinics || [],
    profileId,
  );

  const { clinicDetail } = useClinicDetail(clinicId);

  // Note: Logic to update conversations, messages... will be handled after received event switch_clinic_start
  const handleSwitchClinic = async toClinicId => {
    const res = await dispatch(switchConsultToOtherClinic(consultId, toClinicId));

    await dispatch(contextMenuHide());
    await dispatch(
      updateEntities(WEBCHAT, 'conversation', res?.data?.conversationId, {
        isUpdating: true,
        updatedBy: profileId,
      }),
    );

    await switchClinicModal.hide();
  };

  const handleOpenPrescription = async (pendingPrescription = null) => {
    const countryRes = await dispatch(getCountries());
    const countryList = countryRes?.data || [];

    const serviceMarket = conversation?.latestConsult?.service?.market;
    const country = countryList.find(c => c.iso === serviceMarket);

    const pharmacyListRes = await dispatch(getPharmacies({ marketId: country?.id }));
    const pharmacyList = pharmacyListRes?.data || [];

    if (!pharmacyList?.length) {
      noPharmacyModal.show({ clinicName: clinicDetail?.name });
      return;
    }

    // There are 2 types of pharmacy:
    // - clinic: pharmacy is linked to a clinic -> need to filter if it's linked to current clinic
    // - pharmacy: standalone pharmacy (not linked to a clinic) -> no need to filter
    // (because client sent legalEntityCountryId as marketId)
    const filteredPharmacyList = pharmacyList.filter(pharmacy => {
      if (pharmacy.pharmacyType === 'clinic') return pharmacy.clinicId === clinicId;

      return pharmacy;
    });

    // If cannot find any pharmacy having clinic_id matching the clinic_id of consult -> need to switch clinic
    if (!filteredPharmacyList?.length) {
      switchClinicModal.show({
        clinics: removeDuplicateClinics(
          pharmacyList.map(pharmacy => pharmacy.clinic).filter(clinic => clinic.id !== clinicId),
        ),
        onSwitchClinic: handleSwitchClinic,
      });

      return;
    }

    dispatch(
      addModal({
        type: 'prescriptionModal',
        id: 1,
        data: {
          conversation,
          pendingPrescription: pendingPrescription || null,
          pharmacies: filteredPharmacyList,
        },
      }),
    );
  };

  const onClickIssuePrescription = async () => {
    // Find legal entity (clinic) of the consult
    const legalEntity = (conversation?.latestConsult?.clinics || []).find(
      c => c.doctorId === profileId,
    );

    // Indonesia allows issuing many prescriptions -> Skip checking pending prescription
    if (legalEntity?.country?.iso === INDONESIA_ISO_CODE) {
      await handleOpenPrescription();
      return;
    }

    const pendingPrescriptionRes = await dispatch(getPendingPrescription(consultId));
    if (pendingPrescriptionRes?.data?.length) {
      const pendingPrescription = pendingPrescriptionRes?.data[0];
      dispatch(
        addModal({
          type: 'confirmVoidPrescription',
          id: 1,
          data: {
            conversation,
            pendingPrescription,
          },
        }),
      );

      return;
    }

    await handleOpenPrescription();
  };

  const voidPendingPrescription = async (pendingPrescription, onClose) => {
    await dispatch(voidPrescription(pendingPrescription.id));
    onClose();

    await handleOpenPrescription(pendingPrescription);
  };

  return {
    voidPendingPrescription,
    onClickIssuePrescription,
  };
};

export default useIssuePrescription;
